import _ from 'lodash';

import { InputFormat, ValidationType } from 'types/CommonTypes';

type ValidationTargetType = {
  type: 'required' | 'url' | 'email' | 'tel' | 'number';
  value: string;
};
const expressions = {
  text: /[^(가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9)]/,
  // email: /[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}/,
  email: /^[a-zA-Z0-9]+(?:[_.-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:[_.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,4}$/,
  emailRegex: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
  tel: /^(?:\d{2}-\d{4}-\d{4}|\d{3}-\d{4}-\d{4}|\d{2}-\d{3}-\d{4}|\d{9,11})$/,
  // tel: /\d{2,3}-\d{3,4}-\d{4}/,
  number: /^[0-9\b]+$/,
  // url: /(http(s)?:\/\/)([a-z0-9\w]+\.*)+[a-z0-9]{2,4}/gi,
  // url: /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/gm,
  url: /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/gm,
  roman: /^[a-zA-Z0-9!@#$\-_]+$/,
  password: /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]{8,}$/, // 최소 8자 이상, 대문자 1개 이상, 숫자 1개 이상, 특수문자 허용
  youtube:
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)|(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]+)/,
  timeRange: /^([01]?[0-9]|2[0-3]):?[0-5][0-9]$/,
  // 추가
  lastName: /^[가-힣a-zA-Z\s]{1,20}$/, // 한글, 영문 허용, 1-20자
  firstName: /^[가-힣a-zA-Z\s]{1,20}$/, // 한글, 영문 허용, 1-20자
  countryCode: /^\+\d{1,4}$/, // +1 ~ +9999 형식
  phoneNumber: /^[0-9]{9,11}$/, // 숫자만 9-11자리
  birthday: /^(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/, // YYYY-MM-DD 서버 형식 (!!! 생년월일은 형식 확인 필요)
};

function validityCheck(value: string, format: InputFormat) {
  /**
   * null일경우 유효성 통과이므로 true를 반환한다.
   */
  return value.match((expressions as any)[format]) !== null;
}

function validityCustomCheck(value: string, validation: ValidationType) {
  /**
   * null일경우 유효성 통과이므로 true를 반환한다.
   */
  const result = value.match(validation.expression) !== null;
  return result;
}

/**
 * @description 필수값 체크
 * @param value
 * @returns boolean : true:정상, false:실패
 */
const required = (value?: string | number | Record<string, string>) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }
  if (typeof value === 'object') {
    return Object.values(value).some((v: string | number) => {
      if (typeof v === 'number') {
        return v.toString().length !== 0;
      }
      return v.trim().length !== 0;
    });
  }
  if (typeof value !== 'number') {
    return value?.trim()?.length !== 0;
  } else {
    return value?.toString().length !== 0;
  }
};

/**
 * @description 날자값 체크
 * @param value
 * @returns boolean : true:정상, false:실패
 */
const date = (value?: string) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }
  return true;
  // const dateTime = moment(value);
  // return dateTime.isValid();
};

/**
 * @description 유효시간 체크
 * @param value
 * @returns boolean : true:정상, false:실패
 */
const timeRange = (value?: string) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }
  return value?.match(expressions.timeRange) !== null;
};

/** @description 공백 불가 */
const url = (value?: string) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }
  return value?.match(expressions.url) !== null;
};

/** @description 공백 허용 */
const urlOrEmpty = (value?: string) => {
  if (typeof value === 'undefined' || value?.length === 0 || value === null) {
    return true;
  }
  return value?.match(expressions.url) !== null;
};

const alphanumericWithSymbols = (value?: string) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }
  return value?.match(expressions.roman) !== null;
};

const email = (value?: string) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }
  return value?.match(expressions.email) !== null;
};

const emailRegex = (value?: string) => {
  if (typeof value === 'undefined' || value === null) {
    return false; // 유효하지 않음
  }
  return value?.match(expressions.emailRegex) !== null; // 그 외의 경우 유효성 검사
};

const password = (value?: string) => {
  if (typeof value === 'undefined' || value === '') {
    return false;
  }
  // 최소 8자 이상인지 확인
  return value.length >= 8 && value.match(expressions.password) !== null;
};

const tel = (value?: string) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }
  const result = value?.match(expressions.tel) !== null;
  return result;
};

const number = (value?: string) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }
  const result = value?.match(expressions.number) !== null;
  return result;
};

const youtube = (value?: string) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }
  const result = value?.match(expressions.youtube) !== null;
  return result;
};

const custom = (value: string, format: RegExp | RegExp[]) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }

  // format이 배열일 경우
  if (Array.isArray(format)) {
    for (const regex of format) {
      if (value.match(regex)) {
        return true;
      }
    }
    return false;
  }

  // format이 단일 RegExp일 경우
  return value.match(format) !== null;
};

const validate = (values: Array<ValidationTargetType>) => {
  const t = _.groupBy(values, 'type');
  let result = false;
  if (typeof t.required !== 'undefined') {
    result = !(
      t.required.filter((target: ValidationTargetType) => target?.value?.length === 0).length > 0
    );
  }

  if (typeof t.url !== 'undefined') {
    result =
      t.url.filter(
        (target: ValidationTargetType) =>
          target?.value?.match((expressions as any)[target?.type]) !== null,
      )?.length > 0;

    // result = value.match((expressions as any)[format]) === null ? false : true;
  }
  return result;
};

// 추가
const firstName = (value?: string) => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return false;
  }
  return value.match(expressions.firstName) !== null;
};

const lastName = (value?: string) => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return false;
  }
  return value.match(expressions.lastName) !== null;
};

const countryCode = (value?: string) => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return false;
  }
  return value.match(expressions.countryCode) !== null;
};

const phoneNumber = (value?: string) => {
  if (typeof value === 'undefined' || value === null) {
    return false;
  }
  return value.match(expressions.phoneNumber) !== null;
};

/**
 * @description 생년월일 체크 (단순 정규식 검사. YYYY-MM-DD 형식)
 * @param value
 * @returns boolean : true:정상, false:실패
 */
const birthday = (value?: string) => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return false;
  }
  return value.match(expressions.birthday) !== null;
};

/**
 * @description 생년월일 유효성 검사 (날짜 형식 검사 및 실제로 존재하는 날짜인지 유효성 검사)
 * @param birthday
 * @returns boolean : true:정상, false:실패
 */
const isValidBirthday = (birthday?: string): boolean => {
  if (!birthday || typeof birthday !== 'string') return false;

  // 숫자와 하이픈만 허용하는지 검사
  if (!/^[\d-]*$/.test(birthday)) return false;

  // YYYY-MM-DD 형식 검사
  const dateRegex = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
  if (!dateRegex.test(birthday)) return false;

  // 실제 날짜 유효성 검사
  const [, year, month, day] = birthday.match(dateRegex) || [];
  const date = new Date(Number(year), Number(month) - 1, Number(day));

  return (
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day)
  );
};

const validationHelper = {
  required,
  url,
  urlOrEmpty,
  validate,
  tel,
  email,
  emailRegex,
  password,
  number,
  alphanumericWithSymbols,
  youtube,
  custom,
  date,
  timeRange,
  // 추가
  firstName,
  lastName,
  countryCode,
  phoneNumber,
  birthday, // 단순 정규식 검사
  isValidBirthday, // 정밀한 날짜 유효성 검사
};

export { validityCheck, validityCustomCheck, validationHelper, ValidationTargetType };
